import { getJwtTools } from "@/components/authentication/utils";
import { invalidLatLng } from "@/lib/crg_apis/types/latlng";
import { defaultMaxLssPolygonArea, maxLssPoints } from "@/tools/create/lss-from-map/constants";
import { LssFromMapTutorialProvider, useLssFromMapTutorialState } from "@/tools/create/lss-from-map/context/LssFromMapTutorialContext";
import { CreateLssFromMapSidebar } from "@/tools/create/lss-from-map/sidebar";
import { CreateLssFromMapStage, PointWithMetadata } from "@/tools/create/lss-from-map/types";
import { getLssPointsDensity } from "@/tools/create/lss-from-map/utils";
import { Tool, ToolProps } from "@/types/tools";
import { GatewayOutlined } from "@ant-design/icons";
import * as turf from "@turf/turf";
import { Col, Row, Steps } from "antd";
import classnames from "classnames";
import { Polygon } from "leaflet";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
const PolygonPlotter = dynamic(() => import("./map"), {
  ssr: false
});

// Builds real Leaflet Polygon instances for the tour's demo shapes, so it must stay
// client-only, matching PolygonPlotter above.
const LssFromMapTutorial = dynamic(() => import("@/tools/create/lss-from-map/tutorials/LssFromMapTutorial").then(mod => mod.LssFromMapTutorial), {
  ssr: false
});
const steps: Record<CreateLssFromMapStage, string> = {
  polygon: "Define Large Site",
  points: "Edit Grid Points",
  settings: "Confirm Settings"
};
const LssFromMapSubTool = ({
  ...props
}) => {
  const router = useRouter();
  const [focusPoint, setFocusPoint] = useState(invalidLatLng);
  const [maxLssPolygonArea, setMaxLssPolygonArea] = useState(defaultMaxLssPolygonArea);
  const [points, setPoints] = useState<PointWithMetadata[]>([]);
  const [polygons, setPolygons] = useState<Polygon[]>([]);
  const [stage, setStage] = useState<CreateLssFromMapStage>("polygon");
  const [totalArea, setTotalArea] = useState(0);
  const [refitTrigger, setRefitTrigger] = useState(0);
  const {
    mapContainer,
    areaIndicator,
    tutorialDemoPolygon,
    tutorialDemoPoints,
    setTutorialDemoPoints
  } = useLssFromMapTutorialState();
  useEffect(() => {
    getJwtTools().then(tools => {
      const area = tools?.comprehensive?.largeSite?.maxLssPolygonArea;
      if (area) setMaxLssPolygonArea(area);
    });
  }, []);
  useEffect(() => {
    // Auto-open the tutorial when arriving via the "Start New Run" tour step.
    if (Object.keys(router.query).includes("tutorial")) {
      props.setIsTutorialOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While the tour has set a demo polygon, render/derive everything off of it instead of
  // real user-drawn state, so the real polygon/point pipeline never has to know it's a tutorial.
  const effectivePolygons = tutorialDemoPolygon ? [tutorialDemoPolygon] : polygons;
  const effectivePoints = tutorialDemoPolygon ? tutorialDemoPoints : points;
  const effectiveSetPolygons = tutorialDemoPolygon ? () => {} : setPolygons;
  const effectiveSetPoints = tutorialDemoPolygon ? () => {} : setPoints;

  /** Whether or not the polygons are within acceptable area limits. */
  const isAreaValid = totalArea > 0 && totalArea <= maxLssPolygonArea;
  /** Whether or not the number of points is valid, as well as positions. */
  const arePointsValid = effectivePoints.length > 0 && effectivePoints.length <= maxLssPoints && effectivePoints.every(({
    isInPolygon
  }) => isInPolygon);
  useEffect(() => {
    // Calculate and set the total area of all polygons.
    const newTotalArea = effectivePolygons.reduce<number>((total, polygon) => {
      const geoJson = polygon.toGeoJSON();
      return turf.getType(geoJson) === "Polygon" ? total + turf.area(geoJson) : total;
    }, 0);
    setTotalArea(newTotalArea);
  }, [effectivePolygons]);
  useEffect(() => {
    // Generate demo grid points for the tutorial's demo polygon, matching real point-grid logic.
    if (!tutorialDemoPolygon) {
      setTutorialDemoPoints([]);
      return;
    }
    const feature = tutorialDemoPolygon.toGeoJSON();
    if (turf.getType(feature) !== "Polygon") return;
    const demoFeatures = turf.pointGrid(turf.bbox(feature), getLssPointsDensity(turf.area(feature)), {
      mask: feature,
      units: "meters"
    }).features;
    demoFeatures.forEach((demoPoint, index) => {
      demoPoint.properties = {
        name: `Tutorial Demo Site-${index + 1}`
      };
    });
    setTutorialDemoPoints(demoFeatures.map(point => ({
      point,
      isInPolygon: true
    })));
  }, [tutorialDemoPolygon]);
  return <Row className="w-full" gutter={[0, 16]} data-sentry-element="Row" data-sentry-component="LssFromMapSubTool" data-sentry-source-file="index.tsx">
      <Col span={24} data-sentry-element="Col" data-sentry-source-file="index.tsx">
        <div className="flex items-center justify-between">
          <Steps className="col-2 mx-auto max-w-[600px] justify-self-center [&_.ant-collapse-header]:!mt-0" current={Object.keys(steps).indexOf(stage)} items={[...Object.values(steps).map(title => ({
          title
        })), {
          title: "Review"
        }]} labelPlacement="horizontal" size="small" data-sentry-element="Steps" data-sentry-source-file="index.tsx" />
        </div>
      </Col>
      <Row gutter={[16, 0]} className={classnames(styles.container, "mb-8 w-full")} data-sentry-element="Row" data-sentry-source-file="index.tsx">
        <Col flex={1} data-sentry-element="Col" data-sentry-source-file="index.tsx">
          <div ref={mapContainer}>
            <PolygonPlotter areaIndicatorRef={areaIndicator} className="h-[70vh] w-full overflow-hidden rounded-md border" focusPoint={focusPoint} isAreaValid={isAreaValid} isTutorialDemo={!!tutorialDemoPolygon} maxLssPolygonArea={maxLssPolygonArea} points={effectivePoints} polygons={effectivePolygons} refitTrigger={refitTrigger} stage={stage} setPoints={effectiveSetPoints} setPolygons={effectiveSetPolygons} totalArea={totalArea} data-sentry-element="PolygonPlotter" data-sentry-source-file="index.tsx" />
          </div>
        </Col>
        <Col flex={0} className="w-full max-w-[320px] xl:max-w-[640px]" data-sentry-element="Col" data-sentry-source-file="index.tsx">
          <CreateLssFromMapSidebar arePointsValid={arePointsValid} isAreaValid={isAreaValid} points={effectivePoints} polygons={effectivePolygons} setFocusPoint={setFocusPoint} setPolygons={effectiveSetPolygons} setStage={setStage} stage={stage} data-sentry-element="CreateLssFromMapSidebar" data-sentry-source-file="index.tsx" />
        </Col>
      </Row>
      <LssFromMapTutorial onRequestRefit={() => setRefitTrigger(n => n + 1)} setStage={setStage} onClose={() => props.setIsTutorialOpen(false)} open={props.isTutorialOpen} setState={
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {}} data-sentry-element="LssFromMapTutorial" data-sentry-source-file="index.tsx" />
    </Row>;
};
const createLssFromMapSubTool = {
  id: "lss-from-map",
  category: "create",
  keyPrefix: "create.lssFromMap",
  icon: <GatewayOutlined />,
  hasTutorial: true,
  render: (props: ToolProps) => <LssFromMapTutorialProvider isTutorialOpen={props.isTutorialOpen}>
      <LssFromMapSubTool {...props} />
    </LssFromMapTutorialProvider>,
  maxWidth: "1400px"
} as Tool;
export default createLssFromMapSubTool;